import * as React from 'react';
import { Moon, Sun } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { SidebarTrigger } from './ui/sidebar';

interface Action {
  id: string;
  label: string;
  icon?: React.ReactNode;
  variant?: 'primary' | 'secondary';
  onClick?: () => void;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: Action[];
  avatar?: {
    src?: string;
    name?: string;
    initials?: string;
  };
  className?: string;
}

export function PageHeader({
  title,
  subtitle,
  actions,
  className,
}: PageHeaderProps) {
  const [isDark, setIsDark] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return false;
  });

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    if (newIsDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('v2-theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('v2-theme', 'light');
    }
  };

  React.useEffect(() => {
    const savedTheme = localStorage.getItem('v2-theme');
    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)'
    ).matches;
    const shouldBeDark = savedTheme === 'dark' || (!savedTheme && prefersDark);

    setIsDark(shouldBeDark);
    if (shouldBeDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  return (
    <header
      className={cn(
        'flex flex-wrap items-center justify-between px-4 sm:px-6 lg:px-10 py-3 sm:py-4 border-b border-border bg-background gap-3 shrink-0',
        className
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        <SidebarTrigger className="md:hidden size-9 border border-border shrink-0" />
        <h1 className="font-serif text-2xl sm:text-[28px] md:text-[32px] font-normal leading-tight text-foreground truncate tracking-[-0.02em]">
          {title}
          {subtitle && (
            <span className="text-muted-foreground font-normal ml-2">
              ({subtitle})
            </span>
          )}
        </h1>
      </div>

      <div className="flex items-center gap-2 md:gap-3 shrink-0">
        {actions?.map((action) => (
          <button
            key={action.id}
            onClick={action.onClick}
            className={cn(
              'flex items-center gap-2 px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-md text-sm font-semibold transition-colors',
              action.variant === 'primary'
                ? 'bg-foreground text-background hover:bg-foreground/90'
                : 'border border-input bg-background text-foreground hover:bg-accent'
            )}
          >
            {action.icon}
            <span>{action.label}</span>
          </button>
        ))}

        <Button
          variant="outline"
          size="icon"
          onClick={toggleTheme}
          className="size-9 rounded-full"
        >
          {isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </Button>
      </div>
    </header>
  );
}
